import React, { Suspense, lazy, useState } from "react";
import { FaMoon, FaSun } from "react-icons/fa";
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { LoadingIndicator, LoadingScreen } from "./components/LoadingIndicator";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
import AdvahiniHome from "./pages/AdvahiniHome";
import { AlertProvider } from "./contexts/AlertContext";
import { BackgroundUploadProvider } from "./contexts/BackgroundUploadContext";
import { ConfirmProvider } from "./contexts/ConfirmContext";
import WorkspaceSwitcher from "./components/WorkspaceSwitcher";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { WorkspaceProvider } from "./contexts/WorkspaceContext";
import { IS_LOCAL_ENV, PLAYER_HOSTNAME } from "./config/integrations";
import { isAdmin } from "./utils/roleUtils";
import {
  fetchMaintenanceModeEnabled,
  isMaintenanceModeEnabled,
  MAINTENANCE_MODE_EVENT,
} from "./utils/maintenanceMode";

const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminLanguageManagement = lazy(() => import("./pages/AdminLanguageManagement"));
const AdminCurrencyManagement = lazy(() => import("./pages/AdminCurrencyManagement"));
const AdminPricingManagement = lazy(() => import("./pages/AdminPricingManagement"));
const AdminDebugPanels = lazy(() => import("./pages/AdminDebugPanels"));
const AdminSiteContent = lazy(() => import("./pages/AdminSiteContent"));
const AdminScreenManagement = lazy(() => import("./pages/AdminScreenManagement"));
const AboutUsPage = lazy(() => import("./pages/AboutUsPage"));
const AssetsManagementPage = lazy(() => import("./pages/AssetsManagementPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const ContactSalesPage = lazy(() => import("./pages/ContactSalesPage"));
const ContentReview = lazy(() => import("./pages/ContentReview"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const LoginPage2 = lazy(() => import("./pages/LoginPage2"));
const MaintenanceHome = lazy(() => import("./pages/MaintenanceHome"));
const PaymentManagement = lazy(() => import("./pages/PaymentManagement"));
const PlayerPage = lazy(() => import("./pages/PlayerPage"));
const PlaylistCreatePage = lazy(() => import("./pages/PlaylistCreatePage"));
const PlaylistEditorPage = lazy(() => import("./pages/PlaylistEditorPage"));
const PlaylistsPage = lazy(() => import("./pages/PlaylistsPage"));
const PublicContentPage = lazy(() => import("./pages/PublicContentPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const RefundPolicyPage = lazy(() => import("./pages/RefundPolicyPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const RoleManagement = lazy(() => import("./pages/RoleManagement"));
const SchedulesPage = lazy(() => import("./pages/SchedulesPage"));
const ScreenSettingsPage = lazy(() => import("./pages/ScreenSettingsPage"));
const ScreensPage = lazy(() => import("./pages/ScreensPage"));
const SequencesPage = lazy(() => import("./pages/SequencesPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const SeoLandingPage = lazy(() => import("./pages/SeoLandingPage"));
const SubscriptionManagement = lazy(() => import("./pages/SubscriptionManagement"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const UserDocumentationPage = lazy(() => import("./pages/UserDocumentationPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const WebhookEventsPage = lazy(() => import("./pages/WebhookEventsPage"));

const AppFooter = lazy(() => import("./components/AppFooter"));
const AssetsLibraryModal = lazy(() => import("./components/AssetsLibraryModal"));
const SalesContactModal = lazy(() => import("./components/SalesContactModal"));
const UpgradeModal = lazy(() => import("./components/UpgradeModal"));
const UserPortalTour = lazy(() => import("./components/UserPortalTour"));

const SEO_LANDING_ROUTES = new Set([
  "/digital-signage-software",
  "/android-digital-signage",
  "/windows-digital-signage",
  "/retail-digital-signage",
  "/restaurant-menu-board-software",
  "/corporate-digital-signage",
]);

const AUTH_LIKE_ROUTES = new Set([
  "/login",
  "/admin/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  "/login2",
]);

const ModalLoadingFallback = ({ message = "Loading..." }) => (
  <div className="modal-overlay">
    <div className="modal modal-sm">
      <LoadingIndicator message={message} />
    </div>
  </div>
);

function AppLayout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [showAssetsModal, setShowAssetsModal] = useState(false);
  const [showSalesContactModal, setShowSalesContactModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(isMaintenanceModeEnabled);
  const [tourRestartToken, setTourRestartToken] = useState(null);

  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isPlayerDomain = !IS_LOCAL_ENV && Boolean(PLAYER_HOSTNAME) && host === PLAYER_HOSTNAME;
  const isPlayerRoute = location.pathname.startsWith("/player");
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isMaintenanceRoute = location.pathname === "/maintenance";
  const isAdvahiniHomeRoute = location.pathname === "/";
  const isMarketingRoute =
    isAdvahiniHomeRoute ||
    SEO_LANDING_ROUTES.has(location.pathname) ||
    location.pathname === "/blog" ||
    location.pathname.startsWith("/blog/") ||
    location.pathname === "/user-guide" ||
    location.pathname === "/pricing" ||
    location.pathname === "/about-us" ||
    location.pathname === "/privacy-policy" ||
    location.pathname === "/terms-of-service" ||
    location.pathname === "/refund-policy";
  const isAuthLike = AUTH_LIKE_ROUTES.has(location.pathname);
  const showSidebar = !isPlayerRoute && !isAuthLike && !isMarketingRoute;
  const showFooter = !isPlayerRoute && !showSidebar && !isAuthLike;
  const userIsAdmin = isAdmin(user);
  const isUserPortalRoute = showSidebar && user && !location.pathname.startsWith("/admin");

  React.useEffect(() => {
    const syncMaintenanceMode = () => {
      setMaintenanceMode(isMaintenanceModeEnabled());
    };
    window.addEventListener("storage", syncMaintenanceMode);
    window.addEventListener(MAINTENANCE_MODE_EVENT, syncMaintenanceMode);
    return () => {
      window.removeEventListener("storage", syncMaintenanceMode);
      window.removeEventListener(MAINTENANCE_MODE_EVENT, syncMaintenanceMode);
    };
  }, []);

  React.useEffect(() => {
    fetchMaintenanceModeEnabled()
      .then((enabled) => setMaintenanceMode(enabled))
      .catch(() => setMaintenanceMode(isMaintenanceModeEnabled()));
  }, []);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("upgrade") === "1") {
      setShowUpgradeModal(true);
    }
  }, [location.search]);

  React.useEffect(() => {
    const manifestId = "advahini-player-manifest";
    const appleIconId = "advahini-player-apple-icon";
    let manifestLink = document.getElementById(manifestId);
    let appleIconLink = document.getElementById(appleIconId);

    if (isPlayerRoute) {
      if (!manifestLink) {
        manifestLink = document.createElement("link");
        manifestLink.id = manifestId;
        manifestLink.rel = "manifest";
        document.head.appendChild(manifestLink);
      }
      manifestLink.href = "/manifest.webmanifest";

      if (!appleIconLink) {
        appleIconLink = document.createElement("link");
        appleIconLink.id = appleIconId;
        appleIconLink.rel = "apple-touch-icon";
        document.head.appendChild(appleIconLink);
      }
      appleIconLink.href = "/screen_placeholder.jpg";
      return;
    }

    manifestLink?.remove();
    appleIconLink?.remove();
  }, [isPlayerRoute]);

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search]);

  const closeUpgradeModal = () => {
    setShowUpgradeModal(false);
    const params = new URLSearchParams(location.search);
    if (params.get("upgrade") === "1") {
      params.delete("upgrade");
      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : "",
        },
        { replace: true }
      );
    }
  };

  if (isPlayerDomain) {
    return (
      <Suspense fallback={<LoadingScreen message="Loading player..." />}>
        <PlayerPage />
      </Suspense>
    );
  }

  if ((maintenanceMode && !isAdminRoute) || isMaintenanceRoute) {
    return (
      <div className={`app-shell theme-shell-${theme}`}>
        <main className="main full-width">
          <Suspense fallback={<LoadingScreen message="Loading..." />}>
            <MaintenanceHome showAdminLink={Boolean(userIsAdmin || isAdminRoute)} />
          </Suspense>
        </main>
      </div>
    );
  }

  return (
    <div className={`app-shell theme-shell-${theme}`}>
      {showSidebar && (
        <aside className="sidebar">
          <div className="sidebar-top">
            <Link to={isAdminRoute ? "/admin/dashboard" : "/"} className="logo">
              AdVahini
            </Link>
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
              title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
            >
              {theme === "dark" ? <FaSun size={16} /> : <FaMoon size={16} />}
            </button>
          </div>

          {!isAdminRoute ? (
            <div className="sidebar-actions" style={{ display: "grid", gap: 8, marginBottom: 12 }}>
              <div data-tour-id="workspace-switcher">
                <WorkspaceSwitcher />
              </div>
            </div>
          ) : null}

          <nav>
            {isAdminRoute ? (
              <>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-muted)",
                    marginBottom: "0.5rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Admin
                </div>
                <Link to="/admin/dashboard">Dashboard</Link>
                <Link to="/admin/users">Users</Link>
                <Link to="/admin/roles">Roles</Link>
                <Link to="/admin/subscriptions">Subscriptions</Link>
                <Link to="/admin/payments">Payments</Link>
                <Link to="/admin/webhooks">Webhooks</Link>
                <Link to="/admin/debug">Debug Panels</Link>
                <Link to="/admin/currencies">Currencies</Link>
                <Link to="/admin/pricing">Pricing</Link>
                <Link to="/admin/content-review">Content Review</Link>
                <Link to="/admin/site-content">Site Content</Link>
                <Link to="/admin/screens">Screens</Link>
                <Link to="/admin/languages">Languages</Link>
              </>
            ) : (
              <>
                <Link to="/dashboard" data-tour-id="nav-overview">Overview</Link>
               
                <Link to="/screens" data-tour-id="nav-screens">Screens</Link>
                {/* <Link to="/assets" data-tour-id="nav-assets">Assets</Link> */}
                <Link to="/playlists" data-tour-id="nav-playlists">Playlists</Link>
                <Link to="/schedules" data-tour-id="nav-schedules">Schedules</Link>
                {/* <Link to="/sequences">Sequences</Link> */}
                <Link to="/settings" data-tour-id="nav-settings">Settings</Link>
                <Link
                  to="/analytics"
                  className="sidebar-beta-link"
                  data-tour-id="nav-analytics"
                  data-tooltip="Analytics is in testing phase"
                  aria-label="Analytics beta, in testing phase"
                >
                  <span>Analytics</span>
                  <span className="sidebar-beta-badge">Beta</span>
                </Link>

                <button className="secondary-btn" data-tour-id="nav-assets" onClick={() => setShowAssetsModal(true)}>
                  Assets Library
                </button>
                <button
                  className="secondary-btn sidebar-upgrade-btn"
                  data-tour-id="nav-upgrade"
                  onClick={() => setShowUpgradeModal(true)}
                >
                  Upgrade
                </button>
                {/* {isUserPortalRoute ? (
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => setTourRestartToken(Date.now())}
                  >
                    Start Tour
                  </button>
                ) : null} */}

                {userIsAdmin ? (
                  <Link to="/admin/dashboard">Admin Console</Link>
                ) : null}
              </>
            )}

            {user && (
              <button onClick={logout} className="logout-btn">
                Logout
              </button>
            )}
          </nav>
        </aside>
      )}

      <div className="app-content">
        <main className={`main ${!showSidebar ? "full-width" : ""}`}>
          <Suspense fallback={<LoadingScreen message="Loading page..." />}>
          <Routes>
            <Route
              path="/"
              element={<AdvahiniHome onOpenSalesContact={() => setShowSalesContactModal(true)} />}
            />
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/admin/login"
              element={
                <PublicRoute adminOnly>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <RegisterPage />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPasswordPage />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <ResetPasswordPage />
                </PublicRoute>
              }
            />
            <Route
              path="/verify-email"
              element={
                <PublicRoute>
                  <VerifyEmailPage />
                </PublicRoute>
              }
            />
            <Route
              path="/login2"
              element={
                <PublicRoute>
                  <LoginPage2 />
                </PublicRoute>
              }
            />
            <Route path="/contact-sales" element={<ContactSalesPage />} />
            <Route path="/user-guide" element={<UserDocumentationPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/about-us" element={<AboutUsPage />} />
            <Route path="/digital-signage-software" element={<SeoLandingPage slug="digital-signage-software" />} />
            <Route path="/android-digital-signage" element={<SeoLandingPage slug="android-digital-signage" />} />
            <Route path="/windows-digital-signage" element={<SeoLandingPage slug="windows-digital-signage" />} />
            <Route path="/retail-digital-signage" element={<SeoLandingPage slug="retail-digital-signage" />} />
            <Route path="/restaurant-menu-board-software" element={<SeoLandingPage slug="restaurant-menu-board-software" />} />
            <Route path="/corporate-digital-signage" element={<SeoLandingPage slug="corporate-digital-signage" />} />
            <Route path="/privacy-policy" element={<PublicContentPage slug="privacy-policy" />} />
            <Route path="/terms-of-service" element={<PublicContentPage slug="terms-of-service" />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <AnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/screens"
              element={
                <ProtectedRoute>
                  <ScreensPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/screens/:screenId/settings"
              element={
                <ProtectedRoute>
                  <ScreenSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assets"
              element={
                <ProtectedRoute>
                  <AssetsManagementPage />
                </ProtectedRoute>
              }
            />
            <Route path="/content" element={<Navigate to="/assets" replace />} />
            <Route
              path="/playlists"
              element={
                <ProtectedRoute>
                  <PlaylistsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/playlists/new"
              element={
                <ProtectedRoute>
                  <PlaylistCreatePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/playlists/:playlistId/edit"
              element={
                <ProtectedRoute>
                  <PlaylistEditorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/playlists/:playlistId/config/:sceneId"
              element={
                <ProtectedRoute>
                  <PlaylistEditorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/schedules"
              element={
                <ProtectedRoute>
                  <SchedulesPage />
                </ProtectedRoute>
              }
            />
             <Route
              path="/sequences"
              element={
                <ProtectedRoute>
                  <SequencesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Navigate to="/settings/profile" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/:section"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin>
                  <Navigate to="/admin/dashboard" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute requireAdmin>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/roles"
              element={
                <ProtectedRoute requireAdmin>
                  <RoleManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/subscriptions"
              element={
                <ProtectedRoute requireAdmin>
                  <SubscriptionManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/payments"
              element={
                <ProtectedRoute requireAdmin>
                  <PaymentManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/webhooks"
              element={
                <ProtectedRoute requireAdmin>
                  <WebhookEventsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/debug"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminDebugPanels />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/currencies"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminCurrencyManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pricing"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminPricingManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/content-review"
              element={
                <ProtectedRoute requireAdmin>
                  <ContentReview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/site-content"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminSiteContent />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/screens"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminScreenManagement />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/languages"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminLanguageManagement />
                </ProtectedRoute>
              }
            />

            <Route path="/player" element={<PlayerPage />} />
            <Route path="/player/:registrationCode" element={<PlayerPage />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </main>

        {showFooter ? (
          <Suspense fallback={null}>
            <AppFooter onContactClick={() => setShowSalesContactModal(true)} />
          </Suspense>
        ) : null}
      </div>

      {showAssetsModal ? (
        <Suspense fallback={<ModalLoadingFallback message="Loading assets library..." />}>
          <AssetsLibraryModal isOpen={showAssetsModal} onClose={() => setShowAssetsModal(false)} />
        </Suspense>
      ) : null}
      {showSalesContactModal ? (
        <Suspense fallback={<ModalLoadingFallback message="Loading..." />}>
          <SalesContactModal isOpen={showSalesContactModal} onClose={() => setShowSalesContactModal(false)} />
        </Suspense>
      ) : null}
      {showUpgradeModal ? (
        <Suspense fallback={<ModalLoadingFallback message="Loading upgrade options..." />}>
          <UpgradeModal isOpen={showUpgradeModal} onClose={closeUpgradeModal} />
        </Suspense>
      ) : null}
      {isUserPortalRoute ? (
        <Suspense fallback={null}>
          <UserPortalTour enabled={Boolean(isUserPortalRoute)} user={user} restartToken={tourRestartToken} />
        </Suspense>
      ) : null}
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AlertProvider>
        <ConfirmProvider>
          <BackgroundUploadProvider>
            <LanguageProvider>
              <AuthProvider>
                <WorkspaceProvider>
                  <AppLayout />
                </WorkspaceProvider>
              </AuthProvider>
            </LanguageProvider>
          </BackgroundUploadProvider>
        </ConfirmProvider>
      </AlertProvider>
    </ThemeProvider>
  );
}

export default App;
